// Btc15LiveParts — the pieces that make the BTC 15 tab read as live and as an
// app rather than a report: a freshness badge that shows the age of the data
// to a tenth of a second, a dot on every agent that flashes when it buys or
// sells, sticky sub-tabs, a Coinbase-style agent list, and the activity feed.
//
// Presentational only. The panel decides what each row says (it owns the
// prices, the wagers and the pulse); these draw it. Every flashing thing is
// keyed to an event's age against the SERVER's clock (btc15/pulse.js), so a
// phone with a wrong clock cannot make an old trade look new.
import { memo, useEffect, useRef } from "react";
import { pnlColor } from "./primitives";
import { hapticSuccess, hapticError } from "../../utils/haptics";
import { soundWin } from "../../utils/sound";
import { RADIUS, SPACE, TYPE } from "./tokens";
import { eventAgeMs, eventLine } from "../btc15/pulse";

const mono = TYPE.mono;
const CONFETTI = 64, SADFACES = 24;

// ── keyframes, once ────────────────────────────────────────────────────────
export function Btc15Styles() {
  return (
    <style>{`
      @keyframes b15-ring { 0% { transform: scale(0.7); opacity: 0.85; } 100% { transform: scale(2.6); opacity: 0; } }
      @keyframes b15-beat { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      @keyframes b15-flash { 0% { background-color: var(--b15-flash, rgba(47,216,143,0.28)); } 100% { background-color: transparent; } }
      @keyframes b15-rise { 0% { transform: translateY(10px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
      @keyframes b15-fall { 0% { transform: translateY(-12vh) rotate(0deg); opacity: 0; } 8% { opacity: 1; } 100% { transform: translateY(112vh) rotate(var(--b15-spin, 360deg)); opacity: 0.9; } }
      @keyframes b15-pop { 0% { transform: scale(0.3); opacity: 0; } 55% { transform: scale(1.18); opacity: 1; } 100% { transform: scale(1); opacity: 1; } }
      @keyframes b15-shrink { from { transform: scaleX(1); } to { transform: scaleX(0); } }
      @media (prefers-reduced-motion: reduce) {
        [data-b15-anim] { animation: none !important; }
      }
    `}</style>
  );
}

// ── how fresh the numbers are ──────────────────────────────────────────────
// Green under 2.5 s, amber to 6 s, red past that. The age ticks with the
// tab's clock, so it visibly counts up between pulses and drops back to
// ~0 on each one: that is the proof it is alive, not a claim.
export function liveState(pulseState, nowMs) {
  if (!pulseState?.receivedAt) return { state: "offline", ageMs: null };
  const ageMs = Math.max(0, nowMs - pulseState.receivedAt);
  if (ageMs < 2500) return { state: "live", ageMs };
  if (ageMs < 6000) return { state: "delayed", ageMs };
  return { state: "offline", ageMs };
}

export function LiveBadge({ T, pulseState, nowMs, fromPulse, onTap }) {
  const { state, ageMs } = liveState(pulseState, nowMs);
  const color = state === "live" ? T.green : state === "delayed" ? T.gold || "#f5b942" : T.red;
  const label = state === "live" ? "LIVE" : state === "delayed" ? "DELAYED" : "OFFLINE";
  return (
    <span data-testid="btc15-live-badge" data-state={state} data-from-pulse={fromPulse ? "1" : "0"} onClick={onTap}
      title={state === "live" ? "The market and every position are read from Kalshi's book about once a second" : "The one-second feed is not arriving; numbers may be up to 20 seconds old"}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 8px", borderRadius: RADIUS.pill, cursor: onTap ? "pointer" : "default",
        border: `1px solid ${color}55`, background: `${color}18`, color, fontSize: 10, fontWeight: 900, letterSpacing: 0.6, fontFamily: TYPE.sans }}>
      <span style={{ position: "relative", width: 7, height: 7, display: "inline-block" }}>
        <span data-b15-anim style={{ position: "absolute", inset: 0, borderRadius: "50%", background: color, animation: state === "live" ? "b15-beat 1s ease-in-out infinite" : "none" }} />
        {state === "live" && <span data-b15-anim style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `1px solid ${color}`, animation: "b15-ring 1.4s ease-out infinite" }} />}
      </span>
      {label}
      <span data-testid="btc15-live-age" style={{ fontFamily: mono, fontWeight: 700, opacity: 0.85 }}>
        {ageMs == null ? "—" : `${(ageMs / 1000).toFixed(1)}s`}
      </span>
    </span>
  );
}

// ── the dot on an agent ────────────────────────────────────────────────────
// buy: green, expanding ring · sell: red, expanding ring · in: steady accent ·
// out: hollow. `flashing` is what the ring is for.
export function AgentDot({ T, dot, size = 10, testId }) {
  const state = dot?.state || "out";
  const color = state === "buy" ? T.green : state === "sell" ? T.red : state === "in" ? T.accent : T.faint;
  return (
    <span data-testid={testId} data-state={state} data-flashing={dot?.flashing ? "1" : "0"}
      style={{ position: "relative", display: "inline-block", width: size, height: size, flexShrink: 0 }}>
      <span style={{ position: "absolute", inset: 0, borderRadius: "50%", background: state === "out" ? "transparent" : color, border: `1.5px solid ${color}`, boxSizing: "border-box" }} />
      {dot?.flashing && (
        <>
          <span data-b15-anim style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${color}`, animation: "b15-ring 1s ease-out infinite" }} />
          <span data-b15-anim style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${color}`, animation: "b15-ring 1s ease-out 0.5s infinite" }} />
        </>
      )}
    </span>
  );
}

// ── sticky sub-tabs ────────────────────────────────────────────────────────
export function SubTabs({ T, tabs, value, onChange, bleed = SPACE.lg, background }) {
  return (
    <div data-testid="btc15-subtabs" role="tablist" style={{
      position: "sticky", top: 0, zIndex: 5, display: "flex", gap: 2, margin: `${SPACE.xs}px -${bleed}px ${SPACE.md}px`, padding: `0 ${bleed}px`,
      background: background || T.bgBase, borderBottom: `1px solid ${T.border}`,
    }}>
      {tabs.map((t) => {
        const on = t.id === value;
        return (
          <button key={t.id} type="button" role="tab" aria-selected={on} data-testid={`btc15-tab-${t.id}`} onClick={() => onChange(t.id)}
            style={{ position: "relative", flex: 1, minWidth: 0, padding: "11px 2px 10px", background: "transparent", border: "none", cursor: "pointer",
              color: on ? T.text : T.muted, fontSize: 13.5, fontWeight: on ? 900 : 600, fontFamily: TYPE.sans, letterSpacing: -0.1 }}>
            {t.label}
            {t.badge != null && t.badge > 0 && (
              <span data-testid={`btc15-tab-badge-${t.id}`} style={{ marginLeft: 4, padding: "0 5px", borderRadius: RADIUS.pill, background: `${T.accent}30`, color: T.accent, fontSize: 10, fontWeight: 900, fontFamily: mono }}>{t.badge}</span>
            )}
            <span style={{ position: "absolute", left: 6, right: 6, bottom: -1, height: 3, borderRadius: 2, background: on ? T.accent : "transparent", transition: "background 160ms ease" }} />
          </button>
        );
      })}
    </div>
  );
}

// ── the agent list (Coinbase-style rows) ───────────────────────────────────
// rows: [{ key, name, label, p, pColor, sub, subColor, dot, upnlText, upnlColor, vs }]
export const AgentStrip = memo(function AgentStrip({ T, rows, onOpen }) {
  return (
    <div data-testid="btc15-strip">
      {rows.map((r, i) => (
        <div key={r.key} data-testid={`btc15-strip-row-${r.key}`} onClick={() => onOpen?.(r.key)}
          style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 2px", cursor: "pointer", minWidth: 0,
            borderTop: i ? `1px solid ${T.border}` : "none",
            ...(r.dot?.flashing ? { "--b15-flash": r.dot.state === "buy" ? `${T.green}47` : `${T.red}47`, animation: "b15-flash 2.4s ease-out 1" } : null) }}>
          <span style={{ position: "relative", width: 38, height: 38, borderRadius: "50%", background: `${T.accent}22`, color: T.text, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 15, flexShrink: 0 }}>
            {(r.name || "?").slice(0, 1)}
            <span style={{ position: "absolute", right: -1, bottom: -1, padding: 2, borderRadius: "50%", background: T.bgBase, lineHeight: 0 }}>
              <AgentDot T={T} dot={r.dot} size={10} testId={`btc15-dot-${r.key}`} />
            </span>
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "flex", alignItems: "baseline", gap: 6, minWidth: 0 }}>
              <b style={{ fontSize: 14.5, color: T.text }}>{r.name}</b>
              {r.dot?.flashing && (
                <span data-testid={`btc15-flash-${r.key}`} style={{ fontSize: 9.5, fontWeight: 900, letterSpacing: 0.6, color: r.dot.state === "buy" ? T.green : T.red }}>
                  {r.dot.state === "buy" ? "BUY" : "SELL"}
                </span>
              )}
            </span>
            <span style={{ display: "block", fontSize: 11.5, color: r.subColor || T.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.sub}</span>
          </span>
          <span style={{ textAlign: "right", flexShrink: 0 }}>
            <span style={{ display: "block", fontFamily: mono, fontSize: 17, fontWeight: 900, color: r.pColor || T.text, letterSpacing: -0.5 }}>{r.p}</span>
            <span style={{ display: "block", fontFamily: mono, fontSize: 11, fontWeight: 700, color: r.upnlColor || T.faint }}>{r.upnlText || r.vs || ""}</span>
          </span>
        </div>
      ))}
    </div>
  );
});

// ── "3s", "4m" ─────────────────────────────────────────────────────────────
export const ago = (ms) => {
  if (!Number.isFinite(ms)) return "";
  const s = Math.floor(ms / 1000);
  if (s < 2) return "now";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h`;
};

// ── the activity feed ──────────────────────────────────────────────────────
// Newest first. A row that arrived in the last 3 seconds flashes once.
export function ActivityFeed({ T, events, pulseState, nowMs, nameOf, money, cents, limit = 8, emptyText = "Nothing traded in the last half hour." }) {
  const rows = (events || []).slice(0, limit);
  if (!rows.length) return <div data-testid="btc15-feed-empty" style={{ fontSize: 12, color: T.muted, padding: "10px 0" }}>{emptyText}</div>;
  const tone = { buy: T.green, win: T.green, loss: T.red };
  return (
    <div data-testid="btc15-feed">
      {rows.map((e, i) => {
        const line = eventLine(e, nameOf, money, cents);
        const age = eventAgeMs(e, pulseState, nowMs);
        const color = tone[line.tone] || T.text;
        const fresh = age < 3000;
        return (
          <div key={`${e.lockId}-${e.kind}`} data-testid="btc15-feed-row" data-kind={e.kind}
            style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 2px", minWidth: 0, borderTop: i ? `1px solid ${T.border}` : "none",
              ...(fresh ? { "--b15-flash": `${color}40`, animation: "b15-flash 2.4s ease-out 1" } : null) }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: e.kind === "buy" ? T.green : line.tone === "win" ? T.green : T.red, flexShrink: 0,
              boxShadow: e.kind === "buy" ? "none" : `0 0 0 3px ${(line.tone === "win" ? T.green : T.red)}22` }} />
            <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: T.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              <b>{line.who}</b> <span style={{ color }}>{line.verb}</span> <span style={{ color: T.muted, fontFamily: mono, fontSize: 11.5 }}>{line.detail}</span>
            </span>
            <span style={{ fontFamily: mono, fontSize: 11, color: fresh ? color : T.faint, flexShrink: 0, fontWeight: fresh ? 800 : 500 }}>{ago(age)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── open positions, marked to the live book ────────────────────────────────
// rows: [{ id, name, side, entry, mark, pnlText, pnlColor, sub }]
export function PositionList({ T, rows, emptyText = "No open positions right now." }) {
  if (!rows.length) return <div data-testid="btc15-positions-empty" style={{ fontSize: 12, color: T.muted, padding: "10px 0" }}>{emptyText}</div>;
  return (
    <div data-testid="btc15-positions">
      {rows.map((r, i) => (
        <div key={r.id} data-testid="btc15-position-row" style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 2px", minWidth: 0, borderTop: i ? `1px solid ${T.border}` : "none" }}>
          <span style={{ padding: "2px 7px", borderRadius: RADIUS.sm, background: `${r.side === "yes" ? T.green : T.red}22`, color: r.side === "yes" ? T.green : T.red, fontSize: 10.5, fontWeight: 900, letterSpacing: 0.4, flexShrink: 0 }}>
            {r.side === "yes" ? "UP" : "DOWN"}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <b style={{ fontSize: 13.5, color: T.text }}>{r.name}</b>
            <span style={{ display: "block", fontSize: 11, color: T.faint, fontFamily: mono, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.sub}</span>
          </span>
          <span data-testid="btc15-position-pnl" style={{ fontFamily: mono, fontSize: 14, fontWeight: 900, color: r.pnlColor || T.text, flexShrink: 0 }}>{r.pnlText}</span>
        </div>
      ))}
    </div>
  );
}

// ── full-screen takeover: a result, the moment it lands ───────────────────
// The user's ask, 2026-09-16: confetti on every win, a sad face on every
// loss. 2026-09-19: make it immersive — the whole screen, not a card at the
// bottom of it. A win rains confetti over a green glow; a loss lets sad faces
// fall through a red one. Results arrive in bursts — six agents can resolve on
// one tick — so wins and losses are each coalesced into ONE takeover carrying
// the whole batch, and a chip says how many more are waiting behind it. Tap
// anywhere, or the ✕, to move on; it also moves on by itself, and the 🎉
// toggle in the header turns the whole thing off, because on a busy window
// this fires every couple of minutes.
export function ResultTakeover({ pop, onClose, T, money, cents, nameOf, queued = 0 }) {
  const won = pop?.phase === "won";
  const accent = won ? T.green : T.red;
  // The parent hands over a fresh arrow on every render, and it renders every
  // 250 ms (clock) and every frame (tape). With onClose in the deps the timer
  // was reset each time and the card never left (user, 2026-09-19). Read it
  // through a ref so the timer belongs to the card, not to the render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const ms = pop ? (pop.wagers.length > 2 ? 9000 : 7000) : 0;
  useEffect(() => {
    if (!pop) return undefined;
    if (pop.phase === "won") { hapticSuccess(); soundWin(); } else hapticError();
    const t = setTimeout(() => closeRef.current?.(), ms);
    return () => clearTimeout(t);
  }, [pop]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!pop) return null;
  const fees = pop.wagers.reduce((s2, w) => s2 + Number(w.fee || 0) + Number(w.exitFee || 0), 0);
  const glow = `radial-gradient(120% 80% at 50% 18%, ${accent}${won ? "66" : "4d"} 0%, ${T.bgBase} 64%)`;
  return (
    <div key={pop.key} data-testid="btc15-result-pop" data-phase={pop.phase} onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 60, display: "flex", flexDirection: "column", background: `${glow}, ${T.bgBase}`,
        overflow: "hidden", cursor: "pointer", paddingTop: "env(safe-area-inset-top)" }}>
      {/* the ✕ — inside a fixed, full-screen box, so top-right means top-right of the card. The
          overlay closes on any tap too, so this stops the click from closing a second card. */}
      <button type="button" data-testid="btc15-pop-close" aria-label="Close" onClick={(e) => { e.stopPropagation(); onClose(); }}
        style={{ position: "absolute", top: "calc(env(safe-area-inset-top) + 12px)", right: 14, zIndex: 3, width: 38, height: 38, borderRadius: 19,
          border: `1px solid ${T.border}`, background: T.surface2 || T.surface, color: T.text, cursor: "pointer", fontSize: 16, lineHeight: 1, fontWeight: 800 }}>✕</button>

      {won && [...Array(CONFETTI)].map((_, i) => (
        <span key={i} data-testid="btc15-pop-confetti" data-b15-anim style={{
          position: "absolute", top: 0, left: `${(i * 37) % 100}%`, width: 6 + (i % 4) * 3, height: (i % 3 === 0 ? 14 : 8) + (i % 4) * 2,
          borderRadius: i % 2 ? RADIUS.pill : 2, background: [T.green, T.accent, T.gold, "#ff6ac1", "#ffffff"][i % 5], pointerEvents: "none",
          "--b15-spin": `${(i % 2 ? 1 : -1) * (360 + (i % 5) * 180)}deg`, opacity: 0,
          animation: `b15-fall ${2.6 + (i % 5) * 0.55}s ${(i % 13) * 0.19}s linear infinite`,
        }} />
      ))}
      {!won && [...Array(SADFACES)].map((_, i) => (
        <span key={i} data-testid="btc15-pop-sad" data-b15-anim style={{
          position: "absolute", top: 0, left: `${(i * 43) % 96}%`, fontSize: 20 + (i % 4) * 8, pointerEvents: "none", opacity: 0,
          "--b15-spin": `${(i % 2 ? 1 : -1) * 40}deg`,
          animation: `b15-fall ${5 + (i % 5) * 1.1}s ${(i % 11) * 0.45}s linear infinite`,
        }}>{i % 3 === 0 ? "😢" : "😞"}</span>
      ))}

      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: `${SPACE.lg}px ${SPACE.lg}px 0`, position: "relative", zIndex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 1.4, textTransform: "uppercase", color: T.muted }}>BTC 15 · result</div>
        <div data-b15-anim style={{ fontSize: 96, lineHeight: 1.05, marginTop: 6, animation: "b15-pop 520ms cubic-bezier(0.2, 1.4, 0.4, 1) 1 both" }}>{won ? "🎉" : "😞"}</div>
        <div style={{ fontSize: 30, fontWeight: 900, color: accent, marginTop: 2, letterSpacing: -0.6 }}>
          {won ? "Won" : "Lost"}{pop.wagers.length > 1 ? ` · ${pop.wagers.length} of them` : ""}
        </div>
        <div data-testid="btc15-pop-money" data-b15-anim style={{ fontSize: 60, fontWeight: 900, fontFamily: TYPE.mono, color: accent, letterSpacing: -2.5, marginTop: 2, lineHeight: 1.05,
          animation: "b15-pop 620ms 120ms cubic-bezier(0.2, 1.3, 0.4, 1) 1 both" }}>
          {money(pop.pnl)}
        </div>
        {/* The user's ask, 2026-09-16: net AND gross. The big number is what the
            bank saw; this line is what the trade made and what the fee took. */}
        <div data-testid="btc15-pop-grossnet" style={{ fontSize: 12, fontFamily: TYPE.mono, color: T.muted, marginTop: 6 }}>
          {money(Number(pop.pnl) + fees)} gross · {money(-fees)} fees · <b style={{ color: accent }}>{money(pop.pnl)} net</b>
        </div>

        <div data-testid="btc15-sheet-scroll" onClick={(e) => e.stopPropagation()}
          style={{ flex: "1 1 auto", minHeight: 0, maxHeight: "34dvh", width: "100%", maxWidth: 460, overflowY: "auto", overflowX: "hidden", marginTop: SPACE.md, cursor: "default",
            WebkitOverflowScrolling: "touch", borderRadius: RADIUS.md, background: `${T.bgBase}b3`, border: `1px solid ${T.border}`, padding: `2px ${SPACE.md}px`, alignSelf: "center" }}>
          {pop.wagers.slice(0, 6).map((w, i) => (
            <div key={w.lockId} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "8px 0", borderTop: i ? `1px solid ${T.border}` : "none", fontSize: 12.5 }}>
              <span style={{ minWidth: 0 }}>
                <b>{nameOf(w.agentKey)}</b>
                <span style={{ display: "block", fontSize: 10, color: T.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{w.strategy}</span>
              </span>
              <span style={{ fontFamily: TYPE.mono, color: T.muted, fontSize: 11, textAlign: "right" }}>
                {w.side === "yes" ? "UP" : "DOWN"} @{cents(w.price)}
                <span style={{ display: "block", color: T.faint }}>
                  {w.exitPrice != null ? `out ${cents(w.exitPrice)} · ${(w.closeReason || "").replace(/_/g, " ")}` : "held to the bell"}
                </span>
              </span>
              <span style={{ fontFamily: TYPE.mono, textAlign: "right", whiteSpace: "nowrap" }} data-testid={`btc15-pop-row-${w.lockId}`}>
                <b style={{ color: pnlColor(T, w.pnl) }}>{money(w.pnl)}</b>
                <span style={{ display: "block", fontSize: 9.5, color: T.faint }}>
                  {money(Number(w.pnl || 0) + Number(w.fee || 0) + Number(w.exitFee || 0))} gross · {money(-(Number(w.fee || 0) + Number(w.exitFee || 0)))} fees
                </span>
              </span>
            </div>
          ))}
          {pop.wagers.length > 6 && <div style={{ fontSize: 11, color: T.faint, padding: "6px 0" }}>and {pop.wagers.length - 6} more</div>}
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: `${SPACE.md}px ${SPACE.lg}px calc(${SPACE.lg}px + env(safe-area-inset-bottom))` }}>
        {queued > 0 && (
          <div data-testid="btc15-pop-queued" style={{ display: "inline-block", marginBottom: 8, padding: "3px 10px", borderRadius: RADIUS.pill, background: T.surface2 || T.surface, border: `1px solid ${T.border}`, fontSize: 11, fontWeight: 800, color: T.text }}>
            {queued} more result{queued > 1 ? "s" : ""} waiting
          </div>
        )}
        <div style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>Tap anywhere to continue</div>
        <div style={{ height: 3, borderRadius: 2, background: `${accent}30`, marginTop: 10, overflow: "hidden" }}>
          <div data-b15-anim data-testid="btc15-pop-timer" style={{ height: "100%", background: accent, transformOrigin: "left", animation: `b15-shrink ${ms}ms linear 1 forwards` }} />
        </div>
      </div>
    </div>
  );
}
